import { Loader2, Search, X, MapPin, User, Calendar, Camera, ArrowRight } from "lucide-react";
import { Button } from "@/modern/components/ui/button";
import { Input } from "@/modern/components/ui/input";
import { useEntityFileContext } from "@/react/components/entityViews/files/context/EntityFileContext";
import InfiniteScroll from "@/modern/components/infinite-scroll";
import moment from "moment";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/modern/components/ui/card";
import { useRef, useEffect } from "react";

interface CompanyCamProjectEditorProps {
  onFinish?: () => void;
}

export const CompanyCamProjectEditor = ({ onFinish }: CompanyCamProjectEditorProps) => {
  const {
    entityRecord: record,
    hasCompanyCamToken,
    ccProjects,
    hasMore,
    getCompanyCamProjects,
    loading,
    onSearch,
    searchQuery,
    isResettingProjects,
    setCompanyCamProject,
    unsetCompanyCamProject,
    isFetchingCurrentProject
  } = useEntityFileContext();

  // Always cache non-empty projects
  const prevProjects = useRef(ccProjects);

  useEffect(() => {
    if (ccProjects.length > 0) {
      prevProjects.current = ccProjects;
    }
  }, [ccProjects]);

  // Always show cached projects during loading, switch to new results only when loading completes
  const shouldUseCachedProjects = (loading || isResettingProjects) && prevProjects.current.length > 0;
  const displayProjects = shouldUseCachedProjects ? prevProjects.current : ccProjects;

  return (
    <div className="py-6">
      {/* Search Header */}
      <div className="flex items-center gap-3">
        <div className={`relative flex-1 ${!hasCompanyCamToken && " hidden "} mb-6`}>
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            type="text"
            placeholder="Search Company Cam projects..."
            value={searchQuery}
            onChange={(e) => {
              onSearch(e.target.value);
            }}
            className="pl-10 pr-10"
          />
          {loading && ccProjects.length === 0 && (
            <div className="absolute right-10 top-1/2 transform -translate-y-1/2">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          )}
          {searchQuery && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6"
              onClick={() => {
                onSearch("");
              }}
            >
              <X className="h-3 w-3" />
            </Button>
          )}
        </div>
      </div>

      {!hasCompanyCamToken && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Camera className="h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-muted-foreground mb-2">
            No Company Cam token under Board Settings <ArrowRight className="h-4 w-4 inline-block" /> Integrations <ArrowRight className="h-4 w-4 inline-block" /> Company Cam Token
          </h3>
        </div>
      )}

      {/* Projects Grid */}
      <div className="space-y-4">
        {displayProjects.length === 0 && !loading && hasCompanyCamToken && !isFetchingCurrentProject ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Camera className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium text-muted-foreground mb-2">No projects found</h3>
            <p className="text-sm text-muted-foreground">{searchQuery ? "Try adjusting your search terms" : "No Company Cam projects available"}</p>
          </div>
        ) : (
          <>
            {displayProjects.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {displayProjects.map((cc) => {
                  const isSelected = record.companyCamProject === cc.id;
                  return (
                    <Card
                      key={cc.id}
                      variant={isSelected ? "default" : "outline"}
                      className={`transition-all duration-200 flex flex-col ${isSelected ? "ring-2 ring-primary ring-offset-2 shadow-lg" : "hover:shadow-md"}`}
                    >
                      <div className="relative">
                        <img
                          src={cc.imageUrl}
                          alt={cc.name}
                          className="w-full h-48 object-cover rounded-t-lg"
                          onError={(e) => {
                            e.currentTarget.src = "https://ps.w.org/replace-broken-images/assets/icon-256x256.png";
                          }}
                        />
                        <div className="absolute top-2 right-2">
                          <div className="bg-background/80 backdrop-blur-sm rounded-full p-1">
                            <Camera className="h-4 w-4 text-muted-foreground" />
                          </div>
                        </div>
                        {isSelected && (
                          <div className="absolute top-2 left-2">
                            <div className="bg-primary text-primary-foreground rounded-full p-1">
                              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            </div>
                          </div>
                        )}
                      </div>

                      <CardHeader className="pb-3">
                        <CardTitle className="text-md line-clamp-2">{cc.name || "Untitled"}</CardTitle>
                      </CardHeader>

                      <CardContent className="space-y-3">
                        {/* Address */}
                        <div className="flex items-start gap-2">
                          <MapPin className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                          <div className="text-sm truncate text-muted-foreground">
                            {[cc.address.street_address_1, cc.address.street_address_2, cc.address.city, cc.address.state, cc.address.postal_code, cc.address.country].filter(Boolean).join(", ")}
                          </div>
                        </div>

                        {/* Creator */}
                        {cc.creatorName && (
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">Created by {cc.creatorName}</span>
                          </div>
                        )}

                        {/* Updated Date */}
                        {cc.updatedAt && (
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">Last Updated {moment(Number(cc.updatedAt) * 1000).fromNow()}</span>
                          </div>
                        )}
                      </CardContent>

                      <CardFooter className="mt-auto">
                        <Button
                          variant={isSelected ? "secondary" : "default"}
                          size="sm"
                          className="w-full"
                          onClick={() => {
                            if (isSelected) {
                              unsetCompanyCamProject();
                            } else {
                              setCompanyCamProject(cc.id);
                              onFinish?.();
                            }
                          }}
                        >
                          {isSelected ? "Selected" : "Select Project"}
                        </Button>
                      </CardFooter>
                    </Card>
                  );
                })}
              </div>
            )}
          </>
        )}
        <InfiniteScroll hasMore={hasMore} isLoading={loading} next={getCompanyCamProjects} threshold={1}>
          {hasMore && <div className="flex justify-center py-4">{loading && <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />}</div>}
        </InfiniteScroll>
      </div>
    </div>
  );
};
